import { stripHtml } from "@/lib/citationUtils";
import { autoTitleFromPlainText, fallbackTitleFromPlainText } from "@/lib/autoTitleFromText";
import {
  applyDocumentNamingPreferences,
  shouldUseDescriptorOnlyTitle,
  type DocumentNamingPreferences,
} from "@/lib/documentNamingPreferences";

/** Minimum stripped plain text length before we suggest any title. */
export const DOCUMENT_TITLE_SUGGEST_MIN_BODY = 12;

/** Below this length, use local truncation only (no model call). */
export const DOCUMENT_TITLE_AI_MIN_BODY = 48;

const PLACEHOLDER_TITLES = new Set([
  "",
  "untitled",
  "untitled document",
  "new document",
  "untitled note",
]);

export function isDocumentTitlePlaceholder(title: string): boolean {
  return PLACEHOLDER_TITLES.has(title.trim().toLowerCase());
}

function normalizeTopicTitle(topicTitle: string | null | undefined): string | null {
  const normalized = (topicTitle ?? "").replace(/\s+/g, " ").trim();
  return normalized || null;
}

function extractDescriptor(baseTitle: string): string {
  const normalized = baseTitle.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  const colonIndex = normalized.indexOf(":");
  if (colonIndex === -1) return normalized;
  const descriptor = normalized.slice(colonIndex + 1).trim();
  return descriptor || normalized;
}

function normalizeDescriptor(descriptor: string): string {
  return descriptor
    .replace(/\s+/g, " ")
    .replace(/^[\s\-–—:;,.]+/, "")
    .replace(/[\s\-–—:;,.]+$/, "")
    .trim();
}

/**
 * When a chat topic is already selected, use that topic as the stable title prefix and keep
 * AI/fallback generation focused on the descriptive portion only.
 */
export function formatSuggestedDocumentTitle(
  baseTitle: string,
  topicTitle?: string | null
): string {
  const normalizedBase = baseTitle.replace(/\s+/g, " ").trim();
  const normalizedTopic = normalizeTopicTitle(topicTitle);
  if (!normalizedTopic) return normalizedBase;
  if (!normalizedBase) return normalizedTopic;

  const descriptor = normalizeDescriptor(extractDescriptor(normalizedBase));
  if (!descriptor) return normalizedTopic;
  if (descriptor.toLowerCase() === normalizedTopic.toLowerCase()) return normalizedTopic;
  return `${normalizedTopic}: ${descriptor}`;
}

/**
 * Client-only: suggest a title from HTML or plain document body. Does not persist.
 */
export async function suggestDocumentTitleFromBody(
  htmlOrText: string,
  options?: {
    topicTitle?: string | null;
    createdAt?: Date | string | number | null;
    namingPreferences?: Partial<DocumentNamingPreferences> | null;
  }
): Promise<string> {
  const plain = stripHtml(htmlOrText).replace(/\s+/g, " ").trim();
  if (plain.length < DOCUMENT_TITLE_SUGGEST_MIN_BODY) return "";

  const useDescriptorOnly = shouldUseDescriptorOnlyTitle(options?.namingPreferences);

  if (plain.length < DOCUMENT_TITLE_AI_MIN_BODY) {
    return applyDocumentNamingPreferences(fallbackTitleFromPlainText(plain), {
      topicTitle: options?.topicTitle,
      createdAt: options?.createdAt,
      preferences: options?.namingPreferences,
    });
  }

  const suggested = await autoTitleFromPlainText(
    plain,
    useDescriptorOnly ? "document_descriptor" : "document",
    { fallbackText: plain }
  );
  return applyDocumentNamingPreferences(suggested, {
    topicTitle: options?.topicTitle,
    createdAt: options?.createdAt,
    preferences: options?.namingPreferences,
  });
}
